import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Table, TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { ChatService } from 'src/core/services/chat.service';
import { LoadingService } from 'src/core/services/loading.service';
import { ModalService } from 'src/core/services/modal.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { NotiDetails, TimelineEntry, TimelineResponse } from 'src/core/types/servicehistory.types';
import { TimelineComponent } from '../timeline/timeline.component';
import { DataService } from 'src/core/services/data.service';
import { environment } from 'src/environments/environment';
import { DeviceService } from 'src/core/services/device.service';
import { WidgetService } from 'src/core/services/widget.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-ms-notifications-details',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TimelineComponent, TranslocoModule],
  templateUrl: './ms-notifications-details.component.html',
  styleUrl: './ms-notifications-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MsNotificationsDetailsComponent {

  @ViewChild('table') table: Table;
  readonly businessPartnerMode = environment.businessPartnerMode;

  notificationDetails: NotiDetails;
  notificationSummary: TimelineResponse;
  isLoading: boolean;
  isError: boolean;
  isSummaryLoading: boolean;
  isSummaryError: boolean;
  searchTerm: string;
  searchTermParts: string;
  selectedTab: number = 0;
  screenSize: string = 'Large';

  constructor(
    private selectedSystemService: SelectedsystemService,
    private loadingService: LoadingService,
    private chatService: ChatService,
    private modalService: ModalService,
    private dataService: DataService,
    private deviceService: DeviceService,
    private widgetService: WidgetService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit() {

    this.deviceService.size$.subscribe((size) => {
      this.screenSize = size;
    });

    this.selectedSystemService.$notificationDetails.pipe(
      filter(notifications => notifications && notifications.length > 0)
    ).subscribe(notifications => {
      // Handle the selected notification

      this.notificationDetails = notifications?.filter(detail => detail.id === this.selectedSystemService.$selectedNotification.value)[0];

    });

    this.loadingService.$isNotificationDetailsLoading.subscribe(loading => {
      // Handle loading state
      this.isLoading = loading;
    });

    this.loadingService.$isNotificationDetailsError.subscribe(error => {
      // Handle error state
      this.isError = error;
    });

    // Summary subscription (supports either single TimelineResponse or array cache)
    this.selectedSystemService.$notificationSummary.pipe(
      filter(notifications => notifications && notifications.length > 0)
    ).subscribe(notifications => {
      // Handle the selected notification
      this.notificationSummary = notifications?.filter(detail => detail.id === this.selectedSystemService.$selectedNotification.value)?.[0];

    });

    // Summary loading/error indicators
    this.loadingService.$isNotificationSummaryLoading.subscribe(loading => {
      this.isSummaryLoading = loading;
    });
    this.loadingService.$isNotificationSummaryError.subscribe(error => {
      this.isSummaryError = error;
    });

  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  highlightParts(text: string): string {
    if (!this.searchTermParts || !text) return text;

    const escaped = this.searchTermParts.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }



  retrieveTotalQuantity() {
    const data = this.table?.filteredValue || this.notificationDetails.details.parts;
    return data?.reduce((total, part) => {
      return total + (part.parts_consumed || 0);
    }, 0) || 0;
  }

  retrieveTotalCost() {
    const data = this.table?.filteredValue || this.notificationDetails.details.parts;
    const total = data?.reduce((sum, part) => {
      return sum + part.parts_cost;
    }, 0) || 0;

    return total + " €"; // Optionally format with 2 decimal places
  }

  askChat(part: string) {
    this.widgetService.$chatPipSize.next('maximized');
    const prompt = this.translocoService.translate('notificationDetails.usedParts.prompt.replacePart', {
      partName: part['parts_replaced']
    });
    const isOptionAnswer = shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value);

    this.dataService.searchExact(this.notificationDetails.material_number, this.notificationDetails.serial_number).subscribe({
      next: (res) => {
        this.chatService.addMessage(
          prompt,
          true,
          res.response.ProductFamily,
          isOptionAnswer ? true : null,
          { material: this.notificationDetails.material_number, serial: this.notificationDetails.serial_number }
        );
        this.modalService.$modalChanged.next({ modal: "", open: false });
      }
    })

  }

  switchTab(tabIndex: number) {
    this.selectedTab = tabIndex;
  }

}
